import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPin } from "@phosphor-icons/react";

/**
 * French address autocomplete via api-adresse.data.gouv.fr (BAN — free, no key).
 * Suggestions render BELOW the input in an absolute-positioned panel.
 */
export default function AddressAutocomplete({ value, onChange, placeholder = "12 rue de la Santé, 75014 Paris", label = "Adresse du cabinet", testid = "account-address" }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const timer = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const q = (value || "").trim();
    if (q.length < 3) { setSuggestions([]); return; }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const res = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(q)}&limit=6`);
        const data = await res.json();
        setSuggestions((data.features || []).map((f) => ({ label: f.properties.label, city: f.properties.city, postcode: f.properties.postcode })));
      } catch { setSuggestions([]); }
    }, 220);
    return () => timer.current && clearTimeout(timer.current);
  }, [value]);

  useEffect(() => {
    const onDocClick = (e) => { if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const pick = (s) => { onChange(s.label); setOpen(false); setSuggestions([]); };

  const onKey = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && active >= 0) { e.preventDefault(); pick(suggestions[active]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className="relative" ref={containerRef}>
      <Label className="text-xs tracking-[0.15em] uppercase text-[#5F758F]">{label}</Label>
      <Input
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setActive(-1); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
        placeholder={placeholder}
        autoComplete="off"
        className="mt-1.5 bg-white border-[#E1E9F0]"
        data-testid={testid}
      />
      {open && suggestions.length > 0 && (
        <ul className="absolute z-50 left-0 right-0 mt-1 bg-white border border-[#E1E9F0] rounded-md shadow-lg max-h-64 overflow-y-auto">
          {suggestions.map((s, i) => (
            <li
              key={`${s.label}-${i}`}
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
              onMouseEnter={() => setActive(i)}
              className={`px-3 py-2 text-sm cursor-pointer flex items-center gap-2 ${active === i ? "bg-[#F4F7FB]" : "hover:bg-[#F4F7FB]"}`}
              data-testid={`address-suggestion-${i}`}
            >
              <MapPin size={14} className="text-[#3A8FA5] shrink-0" />
              <span className="text-[#1E3A5F]">{s.label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
